import { getCurrentUserId } from "./token";
import { SearchCategory } from "../constants/searchCategories";

/**
 * The records this user opened most recently, so the Ctrl+K palette can offer
 * them before a single key is typed — the thing you want next is usually the
 * thing you just had.
 *
 * Stored per user id: on a shared machine, one user's recent clients must not
 * appear in the next user's palette.
 *
 * Convenience only — never a dependency. If storage is unavailable (private
 * mode, disabled), every function here degrades to a no-op / empty list rather
 * than throwing.
 */
export interface RecentRecord {
    type: SearchCategory;
    id: number;
    label: string;
    sublabel?: string;
    url: string;
    /** Epoch ms, used only for ordering. */
    at: number;
}

const MAX_RECENT = 8;

const storageKey = (): string => `crx.recentlyViewed.${getCurrentUserId() ?? "anon"}`;

/**
 * The user's recently-opened records, most recent first.
 */
export const getRecentlyViewed = (): RecentRecord[] => {
    try {
        const raw = localStorage.getItem(storageKey());
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Note that the user opened a record. Re-opening something moves it to the top
 * rather than duplicating it.
 * @param record - What was opened. `at` is stamped here.
 */
export const recordVisit = (record: Omit<RecentRecord, "at">): void => {
    try {
        if (!record?.id || !record?.label) return;

        const withoutThis = getRecentlyViewed()
            .filter(existing => !(existing.type === record.type && existing.id === record.id));

        const next = [{ ...record, at: Date.now() }, ...withoutThis].slice(0, MAX_RECENT);
        localStorage.setItem(storageKey(), JSON.stringify(next));
    } catch {
        // Storage unavailable — recents are a nicety, not a feature to fail over.
    }
};

export const clearRecentlyViewed = (): void => {
    try {
        localStorage.removeItem(storageKey());
    } catch {
        // no-op
    }
};
